import { useCallback, useContext, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import BorseContext from "../../context/borse";
import useBorseRequestUpdateMutation from "../../hooks/mutations/useBorseRequestUpdateMutation";
import Icon from "../icon";
import Question from "../question";
import BorseDiscardRequestModal from "./borse-discard-request-modal";
import useFocusOnFirstError from "../../hooks/useFocusOnFirstError";

const BorseStep3 = ({ discardModal, setDiscardModal }) => {
  const {
    survey,
    formData,
    labels,
    campaign,
    currentRequest,
    handlePrevious,
    path,
    schoolType,
    appLang,
  } = useContext(BorseContext);

  const section = useMemo(
    () => survey?.definition?.sections?.find((s) => s.id === path?.[2]),
    [survey, path]
  );

  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    trigger,
    setFocus,
    setValue,
    formState: { errors, touchedFields, dirtyFields },
  } = useForm({
    mode: "onTouched",
    defaultValues: useMemo(() => formData, [formData]),
  });

  useFocusOnFirstError(errors, setFocus);

  const formWatch = watch();

  useEffect(() => reset(formData), [reset, formData]);

  const { mutateAsync: updateRequest, isError } = useBorseRequestUpdateMutation(
    null,
    survey?.definition?.sections?.slice(-1)?.id
  );

  const radioSelfSubmitQuestion = useMemo(
    () => section?.questions?.find((q) => q.questionType === "Radio Self Submit"),
    [section]
  );

  const dynamicallyAddedSection = useMemo(
    () =>
      survey?.definition?.sections?.find(
        (s) =>
          s.id ===
          radioSelfSubmitQuestion?.answers?.find(
            (ans) => Number(ans.id) === Number(formWatch[radioSelfSubmitQuestion?.id])
          )?.nextSectionId
      ),
    [survey, radioSelfSubmitQuestion, formWatch]
  );

  const secondRadioSelfSubmitQuestion = useMemo(
    () => dynamicallyAddedSection?.questions?.find((q) => q.questionType === "Radio Self Submit"),
    [dynamicallyAddedSection]
  );

  const secondDynamicallyAddedSection = useMemo(
    () =>
      survey?.definition?.sections?.find(
        (s) =>
          s.id ===
          secondRadioSelfSubmitQuestion?.answers?.find(
            (ans) => Number(ans.id) === Number(formWatch[secondRadioSelfSubmitQuestion?.id])
          )?.nextSectionId
      ),
    [survey, secondRadioSelfSubmitQuestion, formWatch]
  );

  const thirdRadioSelfSubmitQuestion = useMemo(
    () =>
      secondDynamicallyAddedSection?.questions?.find((q) => q.questionType === "Multiple choice"),
    [secondDynamicallyAddedSection]
  );

  const thirdDynamicallyAddedSectionId = useMemo(
    () =>
      thirdRadioSelfSubmitQuestion?.answers?.findIndex(
        (ans) => Number(ans.id) === Number(formWatch[thirdRadioSelfSubmitQuestion?.id])
      ),
    [thirdRadioSelfSubmitQuestion, formWatch]
  );

  const refundBooleanQuestionId = useMemo(
    () =>
      secondRadioSelfSubmitQuestion &&
      secondRadioSelfSubmitQuestion.answers.findIndex(
        (ans) => Number(ans.id) === Number(formWatch[secondRadioSelfSubmitQuestion?.id])
      ) <
        secondRadioSelfSubmitQuestion.answers.length - 1
        ? secondDynamicallyAddedSection?.questions?.[2]?.id
        : null,
    [secondRadioSelfSubmitQuestion, secondDynamicallyAddedSection, formWatch]
  );

  const isRefundRequired = useMemo(() => {
    if (schoolType === "uni") {
      const value = formWatch?.[refundBooleanQuestionId];
      return Array.isArray(value) ? value.length > 0 : !!value;
    } else return false;
  }, [formWatch, refundBooleanQuestionId, schoolType]);

  const refundRelatedQuestions = useMemo(
    () =>
      secondDynamicallyAddedSection?.questions
        ?.slice(3, refundBooleanQuestionId ? 7 : 6)
        ?.map((q) => q.id),
    [secondDynamicallyAddedSection, refundBooleanQuestionId]
  );

  useEffect(() => {
    /* Clear optional refund fields if refund is not required */
    if (!isRefundRequired) {
      const optionalRefundQuestions = secondDynamicallyAddedSection?.questions?.filter((question) =>
        refundRelatedQuestions.includes(question.id)
      );
      optionalRefundQuestions?.forEach((q) => {
        setValue(`${q.id}`, null);
      });
    }
  }, [isRefundRequired, secondDynamicallyAddedSection, setValue, refundRelatedQuestions]);

  const refundAmountQuestionId = useMemo(
    () => secondDynamicallyAddedSection?.questions?.[3]?.id,
    [secondDynamicallyAddedSection]
  );

  const getRefundAmount = useCallback(
    (values) =>
      isRefundRequired && values?.[refundAmountQuestionId] ? values[refundAmountQuestionId] : null,
    [refundAmountQuestionId, isRefundRequired]
  );

  const onSubmit = async (values) => {
    const refundAmount = getRefundAmount(values);
    await updateRequest({
      survey,
      campaignId: campaign?.id,
      request: currentRequest,
      formData,
      values,
      lastCompletedStep: 2,
      email: currentRequest?.welfareEmail,
      phone: currentRequest?.welfarePhone,
      isRequestForMe: currentRequest?.requestForMe,
      studentFirstName: currentRequest?.studentFirstName,
      studentLastName: currentRequest?.studentLastName,
      school: currentRequest?.school,
      ...(refundAmount ? { refundAmount } : {}),
    });
  };

  const isRefundQuestion = useMemo(
    () =>
      secondDynamicallyAddedSection?.questions
        ?.filter((question) =>
          isRefundRequired ? true : !refundRelatedQuestions.includes(question.id)
        )
        ?.map((question) => question.id)
        ?.includes(refundBooleanQuestionId),
    [
      isRefundRequired,
      refundRelatedQuestions,
      secondDynamicallyAddedSection,
      refundBooleanQuestionId,
    ]
  );

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="form" key={section?.id} noValidate>
        {section?.questions?.map((question) => (
          <Question
            question={question}
            key={question.id}
            register={register}
            error={errors[`${question.id}`]}
            touched={touchedFields[`${question.id}`]}
            isDirty={dirtyFields[`${question.id}`]}
            control={control}
            trigger={trigger}
            labels={labels}
            value={formWatch[`${question.id}`]}
            attachments={survey?.attachments}
            isInline={["Multiple choice", "Radio Self Submit"].includes(question.questionType)}
            locale={appLang?.value}
          />
        ))}
        {dynamicallyAddedSection?.questions?.map((question) => (
          <Question
            question={question}
            key={question.id}
            register={register}
            error={errors[`${question.id}`]}
            touched={touchedFields[`${question.id}`]}
            isDirty={dirtyFields[`${question.id}`]}
            control={control}
            trigger={trigger}
            labels={labels}
            value={formWatch[`${question.id}`]}
            attachments={survey?.attachments}
            isInline={["Multiple choice", "Radio Self Submit"].includes(question.questionType)}
            locale={appLang?.value}
          />
        ))}
        {secondDynamicallyAddedSection?.questions
          ?.filter((question) =>
            refundBooleanQuestionId
              ? isRefundRequired
                ? true
                : !refundRelatedQuestions.includes(question.id)
              : true
          )
          ?.map((question, i) => (
            <div key={question.id}>
              {i === 1 && isRefundQuestion && !!labels?.WELFARE_BORSE_REFUND_OPTION && (
                <div
                  className="form__input-label borse-content__refund-text"
                  dangerouslySetInnerHTML={{
                    __html: labels?.WELFARE_BORSE_REFUND_OPTION,
                  }}
                />
              )}
              <Question
                question={
                  isRefundRequired && refundRelatedQuestions.includes(question.id)
                    ? {
                        ...question,
                        ...(thirdDynamicallyAddedSectionId > 0 ? { minNAnswers: 1 } : {}),
                      }
                    : question
                }
                register={register}
                error={errors[`${question.id}`]}
                touched={touchedFields[`${question.id}`]}
                isDirty={dirtyFields[`${question.id}`]}
                control={control}
                trigger={trigger}
                labels={labels}
                value={formWatch[`${question.id}`]}
                attachments={survey?.attachments}
                isInline={["Multiple choice", "Radio Self Submit"].includes(question.questionType)}
                locale={appLang?.value}
              />
            </div>
          ))}
        <div className="form__buttons-wrapper form__buttons-wrapper--static">
          {isError && (
            <p className="form__error text-center">
              {labels?.WELFARE_BORSE_ERROR_SAVING_REQUEST || "Error saving requests"}
            </p>
          )}
          <div className="form__buttons form__buttons--space-between">
            <button className="link" type="button" onClick={handlePrevious}>
              <Icon name="angle-down" />
              {labels?.WELFARE_FORM_BTN_PREVIOUS || "Previous"}
            </button>
            <button className="button primary form__submit">
              {labels?.WELFARE_FORM_BTN_NEXT || "Next"}
            </button>
          </div>
        </div>
      </form>
      <BorseDiscardRequestModal
        key={!!discardModal}
        discardModal={discardModal}
        setDiscardModal={setDiscardModal}
        formWatch={formWatch}
        lastCompletedStep={1}
      />
    </>
  );
};

export default BorseStep3;
